import { useEffect, useCallback, useRef } from 'react';
import { useMapStore } from '@/stores/mapStore';
import type { UserLocationState, LocationPermissionState } from '@/types';

interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  autoStart?: boolean;
  flyToOnFirstFix?: boolean;
}

const DEFAULT_OPTIONS: GeolocationOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
  autoStart: false,  // Don't auto-request - browsers block non-user-initiated requests
  flyToOnFirstFix: true,
};

export function useGeolocation(options: GeolocationOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const watchIdRef = useRef<number | null>(null);
  const hasFlownToLocationRef = useRef(false);
  // Track the PermissionStatus + its change handler so we can detach the
  // listener on unmount (and before re-attaching) instead of leaking it.
  const permissionStatusRef = useRef<PermissionStatus | null>(null);
  const permissionChangeHandlerRef = useRef<(() => void) | null>(null);

  const {
    userLocationState,
    locationPermission,
    isTrackingLocation,
    locationError,
    setUserLocationState,
    setLocationPermission,
    setIsTrackingLocation,
    setIsFollowingUser,
    setLocationError,
    map,
  } = useMapStore();


  // Check if geolocation is available
  const isGeolocationAvailable = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Stop tracking location - defined early to be used in checkPermission
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTrackingLocation(false);
    setIsFollowingUser(false);
  }, [setIsTrackingLocation, setIsFollowingUser]);

  // Check permission status
  const checkPermission = useCallback(async (): Promise<LocationPermissionState> => {
    if (!isGeolocationAvailable) {
      setLocationPermission('unavailable');
      return 'unavailable';
    }

    // Try to query permission status if available
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        const state = result.state as LocationPermissionState;
        setLocationPermission(state);

        // Detach any previously-registered listener before adding a new one so
        // repeated checkPermission() calls don't stack listeners.
        if (permissionStatusRef.current && permissionChangeHandlerRef.current) {
          permissionStatusRef.current.removeEventListener(
            'change',
            permissionChangeHandlerRef.current
          );
        }

        // Listen for permission changes
        const handleChange = () => {
          const newState = result.state as LocationPermissionState;
          setLocationPermission(newState);
          if (newState === 'denied') {
            stopTracking();
            setLocationError('Location permission denied');
          }
        };
        result.addEventListener('change', handleChange);
        permissionStatusRef.current = result;
        permissionChangeHandlerRef.current = handleChange;

        return state;
      } catch {
        // Permission API not fully supported, will check when actually requesting
        return 'prompt';
      }
    }

    return 'prompt';
  }, [isGeolocationAvailable, setLocationPermission, setLocationError, stopTracking]);

  // Handle successful position
  const handlePosition = useCallback((position: GeolocationPosition) => {
    const { coords, timestamp } = position;

    const locationState: UserLocationState = {
      coordinates: {
        lat: coords.latitude,
        lng: coords.longitude,
      },
      accuracy: coords.accuracy,
      altitude: coords.altitude ?? undefined,
      altitudeAccuracy: coords.altitudeAccuracy ?? undefined,
      heading: coords.heading ?? undefined,
      speed: coords.speed ?? undefined,
      timestamp,
      source: coords.accuracy < 100 ? 'gps' : 'network',
    };

    setUserLocationState(locationState);
    setLocationError(null);
    setLocationPermission('granted');

    // Center on the user on the first fix — but not if the URL carries a shared
    // view/pin (?ll=… / #map=…), so a shared link isn't hijacked by auto-location.
    const hasDeepLink =
      typeof window !== 'undefined' &&
      (/[?&]ll=/.test(window.location.search) || /#map=/.test(window.location.hash));
    if (!hasFlownToLocationRef.current && opts.flyToOnFirstFix && !hasDeepLink && map) {
      hasFlownToLocationRef.current = true;
      map.flyTo({
        center: [coords.longitude, coords.latitude],
        zoom: 15,
        duration: 2000,
      });
    }
  }, [setUserLocationState, setLocationError, setLocationPermission, map, opts.flyToOnFirstFix]);

  // Handle position error
  const handleError = useCallback((error: GeolocationPositionError) => {
    let errorMessage: string;
    let permissionState: LocationPermissionState = locationPermission;

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
        permissionState = 'denied';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information is unavailable. Please enable GPS/location services on your device.';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out. Please ensure GPS/location services are enabled and try again.';
        break;
      default:
        errorMessage = 'An unknown error occurred while getting location.';
    }

    setLocationError(errorMessage);
    setLocationPermission(permissionState);
  }, [locationPermission, setLocationError, setLocationPermission]);

  // Start tracking location
  const startTracking = useCallback(() => {
    if (!isGeolocationAvailable) {
      setLocationError('Geolocation is not supported by this browser. Please use a modern browser with location services.');
      return;
    }

    // Clear any existing watch
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    setIsTrackingLocation(true);
    hasFlownToLocationRef.current = false;

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      handleError,
      {
        enableHighAccuracy: opts.enableHighAccuracy,
        timeout: opts.timeout,
        maximumAge: opts.maximumAge,
      }
    );

    // Start watching position
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      {
        enableHighAccuracy: opts.enableHighAccuracy,
        timeout: opts.timeout,
        maximumAge: opts.maximumAge,
      }
    );
  }, [
    isGeolocationAvailable,
    setLocationError,
    setIsTrackingLocation,
    handlePosition,
    handleError,
    opts,
  ]);

  // Request a single position fix
  const requestLocation = useCallback(() => {
    if (!isGeolocationAvailable) {
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      handlePosition,
      handleError,
      {
        enableHighAccuracy: opts.enableHighAccuracy,
        timeout: opts.timeout,
        maximumAge: opts.maximumAge,
      }
    );
  }, [isGeolocationAvailable, handlePosition, handleError, opts, setLocationError]);

  // Auto-start tracking on mount. Always start when permission is already granted
  // (returning users see their current location immediately, no prompt); also
  // start when autoStart is set, which triggers the browser's one-time permission
  // prompt for first-time visitors. This is what keeps the location shown by
  // default instead of requiring a Locate tap each visit.
  useEffect(() => {
    checkPermission().then((state) => {
      if (opts.autoStart || state === 'granted') {
        startTracking();
      }
    });

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      // Detach the permission-change listener so it doesn't outlive the hook.
      if (permissionStatusRef.current && permissionChangeHandlerRef.current) {
        permissionStatusRef.current.removeEventListener(
          'change',
          permissionChangeHandlerRef.current
        );
        permissionStatusRef.current = null;
        permissionChangeHandlerRef.current = null;
      }
    };
  }, []);

  return {
    location: userLocationState,
    permission: locationPermission,
    isTracking: isTrackingLocation,
    error: locationError,
    isAvailable: isGeolocationAvailable,
    startTracking,
    stopTracking,
    requestLocation,
    checkPermission,
  };
}

// Helper function to format accuracy for display
export function formatAccuracy(meters: number): string {
  if (meters < 10) {
    return `${Math.round(meters)} m`;
  } else if (meters < 100) {
    return `~${Math.round(meters / 10) * 10} m`;
  } else if (meters < 1000) {
    return `~${Math.round(meters / 100) * 100} m`;
  } else {
    return `~${(meters / 1000).toFixed(1)} km`;
  }
}

// Helper to get accuracy description
export function getAccuracyDescription(meters: number): { label: string; color: string } {
  if (meters < 10) {
    return { label: 'Excellent', color: 'text-green-600' };
  } else if (meters < 30) {
    return { label: 'Good', color: 'text-green-500' };
  } else if (meters < 100) {
    return { label: 'Fair', color: 'text-yellow-600' };
  } else if (meters < 500) {
    return { label: 'Poor', color: 'text-orange-500' };
  } else {
    return { label: 'Approximate', color: 'text-red-500' };
  }
}
